import React, { useState } from 'react';
import { Shield, ChevronRight, Info, AlertCircle, CheckCircle, Smartphone, QrCode, Phone } from 'lucide-react';

const OktaVerifySetup = () => {
  const [expandedSection, setExpandedSection] = useState('overview');

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  return (
    <div className="space-y-4">
      {/* Overview Section */}
      <div className="bg-white rounded-xl border-2 border-purple-500 overflow-hidden">
        <button
          onClick={() => toggleSection('overview')}
          className="w-full p-4 flex items-center justify-between bg-purple-50 hover:bg-purple-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-600 text-white rounded-lg">
              <Shield size={20} />
            </div>
            <div className="text-left">
              <div className="font-bold text-purple-900">What is Okta Verify?</div>
              <div className="text-sm text-purple-700 mt-0.5">Multi-factor authentication for CommonSpirit resources</div>
            </div>
          </div>
          <ChevronRight
            size={20}
            className={`text-purple-600 transition-transform ${
              expandedSection === 'overview' ? 'rotate-90' : ''
            }`}
          />
        </button>

        {expandedSection === 'overview' && (
          <div className="p-4 border-t-2 border-gray-200 bg-gray-50 space-y-3">
            <p className="text-gray-700 leading-relaxed">
              <strong>Okta Verify</strong> is a multi-factor authentication (MFA) app that adds an extra layer of security when accessing CommonSpirit resources and services.
            </p>
            <div className="space-y-2 text-sm text-gray-700">
              <p><strong>What requires Okta Verify?</strong></p>
              <ul className="ml-6 space-y-1 mt-2">
                <li className="flex items-start gap-2">
                  <span className="text-purple-600 mt-0.5">•</span>
                  <span>CommonSpirit Google email access</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-600 mt-0.5">•</span>
                  <span>IT support authentication requests</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-600 mt-0.5">•</span>
                  <span>Various CommonSpirit applications and systems</span>
                </li>
              </ul>
              <p className="mt-3">
                Okta Verify provides enhanced security by requiring both:
              </p>
              <ul className="ml-6 space-y-1 mt-2">
                <li className="flex items-start gap-2">
                  <span className="text-purple-600 mt-0.5">•</span>
                  <span>Something you know (your password)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-600 mt-0.5">•</span>
                  <span>Something you have (your phone with Okta Verify)</span>
                </li>
              </ul>
            </div>
            <div className="bg-blue-50 border-l-4 border-blue-500 p-3 rounded mt-4">
              <div className="flex items-start gap-2">
                <Info size={18} className="text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-blue-900">
                  All CommonSpirit staff need to set up Okta Verify for secure access to email and other institutional resources.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Step 1: Download Okta Verify */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <button
          onClick={() => toggleSection('download')}
          className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Smartphone size={20} className="text-green-600" />
            </div>
            <div className="text-left">
              <div className="font-semibold text-gray-900">Step 1: Download Okta Verify</div>
              <div className="text-sm text-gray-600 mt-0.5">Install the app on your smartphone</div>
            </div>
          </div>
          <ChevronRight
            size={20}
            className={`text-gray-400 transition-transform ${
              expandedSection === 'download' ? 'rotate-90' : ''
            }`}
          />
        </button>

        {expandedSection === 'download' && (
          <div className="p-4 border-t-2 border-gray-200 bg-gray-50 space-y-4">
            <div className="space-y-3">
              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <span className="bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">i</span>
                  iPhone/iPad Users
                </h4>
                <p className="text-sm text-gray-700">
                  Search for <strong>"Okta Verify"</strong> in the Apple App Store and download the official Okta Verify app
                </p>
              </div>

              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <span className="bg-green-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">A</span>
                  Android Users
                </h4>
                <p className="text-sm text-gray-700">
                  Search for <strong>"Okta Verify"</strong> in the Google Play Store and download the official Okta Verify app
                </p>
              </div>
            </div>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-3 rounded">
              <div className="flex items-start gap-2">
                <Info size={18} className="text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-blue-900">
                  Make sure to download the official <strong>Okta Verify</strong> app, not third-party alternatives.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Step 2: Access Setup Portal */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <button
          onClick={() => toggleSection('portal')}
          className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Info size={20} className="text-blue-600" />
            </div>
            <div className="text-left">
              <div className="font-semibold text-gray-900">Step 2: Access the Setup Portal</div>
              <div className="text-sm text-gray-600 mt-0.5">Navigate to CommonSpirit Okta</div>
            </div>
          </div>
          <ChevronRight
            size={20}
            className={`text-gray-400 transition-transform ${
              expandedSection === 'portal' ? 'rotate-90' : ''
            }`}
          />
        </button>

        {expandedSection === 'portal' && (
          <div className="p-4 border-t-2 border-gray-200 bg-gray-50 space-y-4">
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="space-y-3">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Access Steps:</h4>
                  <ol className="space-y-3 text-sm text-gray-700">
                    <li className="flex gap-3">
                      <span className="font-bold text-blue-600 flex-shrink-0">1.</span>
                      <span>Open a web browser on your computer</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="font-bold text-blue-600 flex-shrink-0">2.</span>
                      <div className="flex-1">
                        <span>Navigate to:</span>
                        <div className="bg-gray-100 p-2 rounded mt-1 font-mono text-xs break-all">
                          https://commonspiritcorp.okta.com
                        </div>
                      </div>
                    </li>
                    <li className="flex gap-3">
                      <span className="font-bold text-blue-600 flex-shrink-0">3.</span>
                      <span>Log in with your CommonSpirit credentials (username and password)</span>
                    </li>
                  </ol>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Step 3: Set Up MFA */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <button
          onClick={() => toggleSection('setup-mfa')}
          className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <QrCode size={20} className="text-purple-600" />
            </div>
            <div className="text-left">
              <div className="font-semibold text-gray-900">Step 3: Set Up Multi-Factor Authentication</div>
              <div className="text-sm text-gray-600 mt-0.5">Configure Okta Verify on your device</div>
            </div>
          </div>
          <ChevronRight
            size={20}
            className={`text-gray-400 transition-transform ${
              expandedSection === 'setup-mfa' ? 'rotate-90' : ''
            }`}
          />
        </button>

        {expandedSection === 'setup-mfa' && (
          <div className="p-4 border-t-2 border-gray-200 bg-gray-50 space-y-4">
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Configuration Steps:</h4>
                  <ol className="space-y-4 text-sm text-gray-700">
                    <li className="flex gap-3">
                      <span className="font-bold text-purple-600 flex-shrink-0">1.</span>
                      <div className="flex-1">
                        <p className="mb-2"><strong>Select Authentication Method</strong></p>
                        <p>When prompted, choose <strong>"Okta Verify"</strong> as your authentication method</p>
                      </div>
                    </li>

                    <li className="flex gap-3">
                      <span className="font-bold text-purple-600 flex-shrink-0">2.</span>
                      <div className="flex-1">
                        <p className="mb-2"><strong>Choose Device Type</strong></p>
                        <p>Select your smartphone type:</p>
                        <ul className="mt-2 ml-4 space-y-1">
                          <li className="flex items-center gap-2">
                            <span className="text-purple-600">•</span>
                            <span><strong>iPhone</strong> - for iOS devices</span>
                          </li>
                          <li className="flex items-center gap-2">
                            <span className="text-purple-600">•</span>
                            <span><strong>Android</strong> - for Android devices</span>
                          </li>
                        </ul>
                      </div>
                    </li>

                    <li className="flex gap-3">
                      <span className="font-bold text-purple-600 flex-shrink-0">3.</span>
                      <div className="flex-1">
                        <p className="mb-2"><strong>Scan QR Code</strong></p>
                        <div className="space-y-2">
                          <p>A QR code will appear on your computer screen</p>
                          <ol className="ml-4 mt-2 space-y-2 text-xs">
                            <li>• Open the Okta Verify app on your smartphone</li>
                            <li>• Tap the <strong>"+"</strong> button to add an account</li>
                            <li>• Select <strong>"Organization"</strong></li>
                            <li>• Point your phone's camera at the QR code on your computer</li>
                            <li>• The app will automatically scan and configure</li>
                          </ol>
                        </div>
                      </div>
                    </li>

                    <li className="flex gap-3">
                      <span className="font-bold text-purple-600 flex-shrink-0">4.</span>
                      <div className="flex-1">
                        <p className="mb-2"><strong>Verify Setup</strong></p>
                        <p>You'll receive a push notification on your phone asking you to approve the setup. Tap <strong>"Approve"</strong> or <strong>"Yes, it's me"</strong></p>
                      </div>
                    </li>

                    <li className="flex gap-3">
                      <span className="font-bold text-purple-600 flex-shrink-0">5.</span>
                      <div className="flex-1">
                        <p className="mb-2"><strong>Complete Setup</strong></p>
                        <p>Once approved, your browser will confirm that MFA setup is complete</p>
                      </div>
                    </li>
                  </ol>
                </div>
              </div>
            </div>

            <div className="bg-green-50 border-l-4 border-green-500 p-3 rounded">
              <div className="flex items-start gap-2">
                <CheckCircle size={18} className="text-green-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-green-900">
                  <p className="font-semibold mb-1">Setup Complete!</p>
                  <p>You can now use Okta Verify to authenticate when accessing Epic ePCS.</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Step 4: Phone Backup (Optional) */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <button
          onClick={() => toggleSection('phone-backup')}
          className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Phone size={20} className="text-orange-600" />
            </div>
            <div className="text-left">
              <div className="font-semibold text-gray-900">Step 4: Add Phone Backup (Recommended)</div>
              <div className="text-sm text-gray-600 mt-0.5">Set up SMS/Voice call as backup method</div>
            </div>
          </div>
          <ChevronRight
            size={20}
            className={`text-gray-400 transition-transform ${
              expandedSection === 'phone-backup' ? 'rotate-90' : ''
            }`}
          />
        </button>

        {expandedSection === 'phone-backup' && (
          <div className="p-4 border-t-2 border-gray-200 bg-gray-50 space-y-4">
            <div className="bg-amber-50 border-l-4 border-amber-500 p-3 rounded mb-4">
              <div className="flex items-start gap-2">
                <AlertCircle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-900">
                  <p className="font-semibold mb-1">Why add a backup method?</p>
                  <p>If you lose your phone or can't access Okta Verify, you'll need a backup authentication method to access Epic.</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="space-y-3">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Setup Backup Authentication:</h4>
                  <ol className="space-y-3 text-sm text-gray-700">
                    <li className="flex gap-3">
                      <span className="font-bold text-orange-600 flex-shrink-0">1.</span>
                      <span>In the Okta setup portal, look for an option to add additional authentication methods</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="font-bold text-orange-600 flex-shrink-0">2.</span>
                      <span>Select <strong>"SMS Authentication"</strong> or <strong>"Voice Call Authentication"</strong></span>
                    </li>
                    <li className="flex gap-3">
                      <span className="font-bold text-orange-600 flex-shrink-0">3.</span>
                      <span>Enter your mobile phone number</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="font-bold text-orange-600 flex-shrink-0">4.</span>
                      <span>You'll receive a verification code via SMS or voice call</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="font-bold text-orange-600 flex-shrink-0">5.</span>
                      <span>Enter the code to verify your phone number</span>
                    </li>
                  </ol>
                </div>

                <div className="bg-blue-50 p-3 rounded mt-4">
                  <p className="text-sm text-blue-900">
                    <strong>How to use backup method:</strong> When logging in, if you can't use Okta Verify, click "Use a different method" and select SMS or Voice call to receive a one-time code.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Key Information */}
      <div className="bg-blue-50 rounded-lg p-4 border-l-4 border-blue-600">
        <h3 className="font-bold text-blue-900 mb-2 flex items-center gap-2">
          <Info size={18} />
          Key Information
        </h3>
        <ul className="space-y-1 text-sm text-blue-900">
          <li>• Setup URL: <span className="font-mono bg-white px-2 py-0.5 rounded">commonspiritcorp.okta.com</span></li>
          <li>• Required for: CommonSpirit email, IT authentication, and various institutional resources</li>
          <li>• Authentication expires: Never (unless you remove the device)</li>
          <li>• Internet required: Yes, for push notifications</li>
        </ul>
      </div>

      {/* Troubleshooting */}
      <div className="bg-amber-50 rounded-lg p-4 border-l-4 border-amber-600">
        <h3 className="font-bold text-amber-900 mb-2 flex items-center gap-2">
          <AlertCircle size={18} />
          Troubleshooting
        </h3>
        <ul className="space-y-2 text-sm text-amber-900">
          <li className="flex items-start gap-2">
            <span className="font-bold flex-shrink-0">•</span>
            <span>
              <strong>Not receiving push notifications?</strong> Check that Okta Verify has notification permissions enabled and that your phone has internet connectivity
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="font-bold flex-shrink-0">•</span>
            <span>
              <strong>QR code won't scan?</strong> Try increasing screen brightness or entering the setup key manually (there's usually a text option)
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="font-bold flex-shrink-0">•</span>
            <span>
              <strong>Lost your phone?</strong> Use your backup SMS/Voice authentication method, then contact IT to reconfigure Okta Verify on your new device
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="font-bold flex-shrink-0">•</span>
            <span>
              <strong>Still having issues?</strong> Contact BSLMC IT Support at 832-355-8000
            </span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default OktaVerifySetup;
